import { VideoTexture } from "@babylonjs/core/Materials/Textures/videoTexture.js";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial.js";
import { waitFor } from "../../utils/AsyncUtils";
export class VideoPreviewPlane {
    constructor(videoPreviewMesh, shape, stateChangedCallback) {
        this._videoTexture = null;
        this._videoMaterial = null;
        this._url = null;
        this._state = "paused";
        this._changingState = false;
        this._onLoadObserver = null;
        this._videoPreviewMesh = videoPreviewMesh;
        this._stateChangedCallback = stateChangedCallback;
        const epsilon = 0.000001;
        this._cornerRadiusWidthFraction = shape.width < epsilon
            ? 0 : shape.cornerRadius / shape.width;
        this._widthDivHeightRatio = (shape.width < epsilon || shape.height < epsilon)
            ? epsilon : shape.width / shape.height;
        this._changeState("paused", true);
    }
    dispose() {
        this._videoMaterial?.dispose();
        this._videoTexture?.dispose();
        this._videoMaterial = null;
        this._videoTexture = null;
    }
    _changeState(newState, force = false) {
        if (!force && newState === this._state)
            return;
        this._state = newState;
        if (this._stateChangedCallback)
            this._stateChangedCallback(this._state);
    }
    setSourceUrl(url) {
        this._url = url ?? null;
    }
    async _pauseInternal() {
        if (this._videoTexture === null)
            return;
        await waitFor(() => !this._changingState, VideoPreviewPlane._changingStateTimeout);
        this._videoTexture.onLoadObservable.remove(this._onLoadObserver);
        this._videoTexture.video.pause();
    }
    async _playInternal() {
        if (this._videoTexture) {
            this._videoTexture.dispose();
            this._videoTexture = null;
        }
        if (this._videoMaterial !== null) {
            this._videoMaterial.dispose();
            this._videoMaterial = null;
        }
        if (this._url !== null) {
            this._videoTexture = new VideoTexture(`${this._videoPreviewMesh.name}Texture`, this._url, this._videoPreviewMesh.getScene(), false, false, VideoTexture.TRILINEAR_SAMPLINGMODE, {
                autoPlay: false,
                loop: true,
                muted: true,
            });
            this._onLoadObserver = this._videoTexture.onLoadObservable.add(async (texture) => {
                await waitFor(() => !this._changingState, VideoPreviewPlane._changingStateTimeout);
                // console.log(`%c++++ VideoPreviewPlane._state: ${this._state}`, "background-color: lime");
                if (this._state === "preparing" && texture === this._videoTexture) {
                    this._changingState = true;
                    try {
                        // await delayAsync(2_000); //TODO DZZ DCVR-5579 Remove!
                        if (this._state === "preparing" && texture === this._videoTexture) {
                            texture.video.loop = true;
                            texture.video.muted = true;
                            await texture.video.play();
                            this._changeState("playing");
                        }
                    }
                    finally {
                        this._changingState = false;
                    }
                }
            });
            this._videoMaterial = VideoPreviewPlane._createVideoMaterial(`${this._videoPreviewMesh.name}Material`, this._videoTexture, this._cornerRadiusWidthFraction, this._widthDivHeightRatio);
            this._videoPreviewMesh.material = this._videoMaterial;
        }
    }
    play() {
        this._changeState("preparing");
        (async () => {
            await this._playInternal();
        })();
    }
    pause() {
        this._changeState("paused");
        (async () => {
            await this._pauseInternal();
        })();
    }
    static _createVideoMaterial(name, videoTexture, cornerRadiusWidthFraction, widthDivHeightRatio) {
        const result = new ShaderMaterial(name, videoTexture.getScene(), {
            vertexSource: `
                    precision highp float;
                    attribute vec3 position;
                    attribute vec2 uv;
                    uniform mat4 worldViewProjection;
                    varying vec2 vUV;
                    void main(void) {
                        gl_Position = worldViewProjection * vec4(position, 1.0);
                        vUV = uv;
                    }`,
            fragmentSource: `
                    precision highp float;
                    uniform sampler2D textureSampler;
                    varying vec2 vUV;
                    uniform float cornerRadiusWidthFraction;
                    uniform float widthDivHeightRatio;
 
                    bool isDiscarded(vec2 uv, float r, float ratio) {
                        float x = uv.x;
                        float y = uv.y;
                        if (x > 0.5)
                            x = 1.0 - x;
                        if (y > 0.5)
                            y = 1.0 - y;
                        y /= ratio;
                        if (x < r && y < r) {
                            vec2 d = vec2(x-r, y-r);
                            if (dot(d, d) > r*r)
                                return true;
                        }
                        return false;
                    }

                    void main(void) {
                        vec2 uv = vUV.xy;
                        if (isDiscarded(uv, cornerRadiusWidthFraction, widthDivHeightRatio)) {
                            discard;
                        } else {
                            vec4 sampleColor = texture2D(textureSampler, uv);
                            gl_FragColor = vec4(sampleColor.rgb, 1.0);
                        }
                    }`,
        }, {
            attributes: ["position", "uv"],
            uniforms: ["world", "worldView", "worldViewProjection", "view", "projection", "textureSampler", "cornerRadiusWidthFraction", "widthDivHeightRatio"],
            // needAlphaBlending: true,
        });
        result.setTexture("textureSampler", videoTexture);
        result.setFloat("cornerRadiusWidthFraction", cornerRadiusWidthFraction);
        result.setFloat("widthDivHeightRatio", widthDivHeightRatio);
        result.backFaceCulling = false;
        return result;
    }
}
VideoPreviewPlane._changingStateTimeout = 10_000;
